import { IsVirtualLocation } from './locationsTree';
import { NormalizeRack } from './payload';

/**
 * Filtro de ÁREA da página de Localizações (r4): chips
 * "Todas / Virtuais / áreas do layout salvo" filtrando LISTAGEM e MAPA juntos
 * (no mapa o recorte é aplicado pelo próprio WarehouseMap via prop
 * `areaFilter` — FilterLayoutByArea segue exportado como o helper puro desse
 * recorte).
 *
 * O vínculo localização→área só existe através do layout desenhado no editor:
 *  - posições de RACK e a cabeceira (STG-A-00) têm `rack` (letra) → rack do
 *    layout (`racks[].rack`) → área (`racks[].areaId`);
 *  - DOCK/STAGING_DOCK e FACTORY_IN/OUT não têm rack → inferência pelo TIPO da
 *    área (`doca` / `producao`) — melhor sinal disponível até o M4/M5;
 *  - INCIDENT não tem vínculo com o mapa → aparece apenas em "Todas";
 *  - VIRTUAL nunca é de área física → aparece apenas em "Todas" e "Virtuais".
 *
 * Layout salvo antes do conceito de rack não tem `racks[]`: nada casa por rack
 * e a chave legada do layout antigo sai do recorte — nunca quebra.
 */

export const AREA_CHIP_ALL = 'all';
export const AREA_CHIP_VIRTUAL = 'virtual';

// Tipos sem rack → tipo de área do layout que os abriga.
const TYPE_TO_AREA_TYPE = {
    DOCK: 'doca',
    STAGING_DOCK: 'doca',
    FACTORY_IN: 'producao',
    FACTORY_OUT: 'producao',
};

/**
 * Trava do filtro de área por permissão do usuário.
 *
 * HOJE retorna sempre `null` (nenhuma trava): papéis restritos a uma área
 * chegam com o coletor no M4/M5. Quando a permissão existir, esta função deve
 * devolver o identificador da área (id da área do layout OU o `type` dela —
 * o chamador compara contra os dois) e a UI já reage: o chip correspondente
 * nasce SELECIONADO e DESABILITADO, e os demais chips desabilitam.
 */
export function LockedAreaForUser(permissions) {
    void permissions; // shape futuro: procurar algo como 'wms:area:<id|type>'
    return null;
}

function layoutAreas(layout) {
    return Array.isArray(layout?.areas) ? layout.areas : [];
}

function layoutRacks(layout) {
    return Array.isArray(layout?.racks) ? layout.racks : [];
}

function layoutBlocks(layout) {
    return Array.isArray(layout?.blocks) ? layout.blocks : [];
}

/**
 * Chips do filtro na ordem do contrato r4: Todas / Virtuais / uma por ÁREA do
 * layout salvo. Texto = label da área (quando o dono nomeou) ou o rótulo do
 * tipo; nomes repetidos ganham sufixo numérico para não virar chips gêmeos.
 */
export function BuildAreaChips(layout, label) {
    const chips = [
        { id: AREA_CHIP_ALL, text: label('wms.locations.area_all'), areaId: null },
        { id: AREA_CHIP_VIRTUAL, text: label('wms.locations.area_virtual'), areaId: null },
    ];
    const seen = new Map();
    for (const area of layoutAreas(layout)) {
        if (!area || area.id === undefined || area.id === null) {
            continue;
        }
        const baseText =
            typeof area.label === 'string' && area.label.trim()
                ? area.label.trim()
                : label(`wms.map.area_type.${area.type}`);
        const count = (seen.get(baseText) ?? 0) + 1;
        seen.set(baseText, count);
        chips.push({
            id: String(area.id),
            text: count > 1 ? `${baseText} ${count}` : baseText,
            areaId: area.id,
            areaType: area.type,
        });
    }
    return chips;
}

// Área B contida geometricamente na área A (staging dentro de Logística etc.).
function areaContains(outer, inner) {
    return (
        inner.id !== outer.id &&
        Number(inner.x1) >= Number(outer.x1) &&
        Number(inner.y1) >= Number(outer.y1) &&
        Number(inner.x2) <= Number(outer.x2) &&
        Number(inner.y2) <= Number(outer.y2)
    );
}

/**
 * Contexto de match da LISTAGEM para uma área selecionada: conjunto de áreas
 * (a escolhida + as contidas nela), tipos presentes e índice rack→área
 * (prefixo em caixa alta → id da área). `null` quando não há área selecionada
 * (chip Todas/Virtuais).
 */
export function BuildAreaContext(layout, areaId) {
    if (!layout || areaId === null || areaId === undefined) {
        return null;
    }
    const areas = layoutAreas(layout);
    const selected = areas.find((area) => String(area?.id) === String(areaId));
    if (!selected) {
        return null;
    }
    const areaSet = new Set([String(selected.id)]);
    const areaTypes = new Set([selected.type]);
    for (const area of areas) {
        if (area && areaContains(selected, area)) {
            areaSet.add(String(area.id));
            areaTypes.add(area.type);
        }
    }
    const rackIndex = new Map();
    for (const rack of layoutRacks(layout)) {
        const prefix = NormalizeRack(rack?.rack);
        if (prefix !== null && rack?.areaId !== undefined && rack?.areaId !== null) {
            rackIndex.set(prefix, String(rack.areaId));
        }
    }
    return { areaSet, areaTypes, rackIndex };
}

/** A localização pertence à área do contexto? (regras no cabeçalho do módulo) */
export function LocationMatchesArea(row, context) {
    if (!context) {
        return true;
    }
    if (IsVirtualLocation(row)) {
        return false;
    }
    const rack = NormalizeRack(row?.rack);
    if (rack !== null) {
        const areaId = context.rackIndex.get(rack);
        return areaId !== undefined && context.areaSet.has(areaId);
    }
    const areaType = TYPE_TO_AREA_TYPE[row?.type];
    return areaType ? context.areaTypes.has(areaType) : false;
}

/**
 * Recorte do LAYOUT para o minimapa quando um chip de área está ativo: mantém
 * a área selecionada (+ contidas), os racks dessas áreas e os blocos desses
 * racks (`blocks[].rackId`). O grid fica intacto — o `fit` do WarehouseMap
 * cuida do enquadramento. Uma chave legada `streets` (layout antigo) é
 * descartada do resultado.
 */
export function FilterLayoutByArea(layout, areaId) {
    if (!layout || areaId === null || areaId === undefined) {
        return layout;
    }
    const context = BuildAreaContext(layout, areaId);
    if (!context) {
        return layout;
    }
    const areas = layoutAreas(layout).filter((area) => context.areaSet.has(String(area?.id)));
    const racks = layoutRacks(layout).filter((rack) => context.areaSet.has(String(rack?.areaId)));
    const rackIds = new Set(racks.map((rack) => String(rack.id)));
    const blocks = layoutBlocks(layout).filter((block) => rackIds.has(String(block?.rackId)));
    const { streets: _legacyStreets, ...rest } = layout;
    return { ...rest, areas, racks, blocks };
}
